import {memo} from 'react'
import {NotificationType, UserNotification} from '@/features/notifications/types'
import {
    StyledMarkReadButton,
    StyledNotificationItem,
    StyledNotificationTime,
    StyledNotificationUnreadDot
} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {useMarkNotificationAsRead} from '@/features/notifications/queries/useMarkNotificationAsRead'
import {formatNotificationDate} from '@/features/notifications/utils/dateFormatters'
import {
    clearManuallyUnread,
    hasBeenSeen,
    markAsManuallyUnread,
    markAsSeen,
    wasManuallyMarkedUnread
} from '@/features/notifications/utils/seenNotifications'
import {useNavigate} from 'react-router-dom'
import {CalendarIcon, CheckDone01Icon, MessageChatSquareIcon, Monitor01Icon} from '@/components/ui/icon/Icon'
import {useMarkNotification} from '../../hooks/useMarkNotification'
import {useTranslation} from '@/translations/i18n'
import {useGetUser} from '@/features/users/queries/useGetUser'

interface NotificationItemProps {
    notification: UserNotification
    onClick?: (notification: UserNotification) => void
}

const NotificationItemComponent = ({notification, onClick}: NotificationItemProps) => {
    const {t} = useTranslation()
    const {mutate: markAsRead} = useMarkNotificationAsRead()
    const {mutate: markNotification} = useMarkNotification({urlParams: {notificationId: notification.id}})
    const navigate = useNavigate()

    const {title, type, severity, sentAt, extraData, triggeredByUser, link, bodyTemplate} = notification.notification

    const formattedTime = formatNotificationDate(sentAt)

    const isSystemTriggered = !triggeredByUser?.id || triggeredByUser.id === 'system'
    const hasTriggeredByName = Boolean(triggeredByUser?.firstName || triggeredByUser?.lastName)
    // Live (socket) notifications only carry the triggering user's id, not their
    // name — resolve it here instead of showing a fake "System" sender.
    const {data: resolvedTriggeredByUser} = useGetUser({
        userId: triggeredByUser?.id,
        responseType: 'small',
        options: {enabled: !isSystemTriggered && !hasTriggeredByName}
    })
    const triggeredByUserName = isSystemTriggered
        ? t('notifications:system_sender')
        : hasTriggeredByName
          ? `${triggeredByUser?.firstName} ${triggeredByUser?.lastName}`.trim()
          : resolvedTriggeredByUser?.fullName ?? ''

    // Conv-001/Conv-002 = conversation notifications. The BE's generic `title` doesn't
    // name the conversation, so we render a two-line title (label + conversation name)
    // from extraData.title when the BE sends one, falling back to the raw BE title
    // otherwise.
    const conversationTitleLabel =
        type === NotificationType.Enum['Conv-001']
            ? t('notifications:added_to_conversation_label')
            : type === NotificationType.Enum['Conv-002']
              ? t('notifications:new_message_in_conversation_label')
              : null
    const hasConversationTitle = Boolean(conversationTitleLabel && extraData?.title)

    // Re-evaluated on every render, so it stays in sync once the "Segna da
    // leggere" toggle below updates readAt and this component re-renders.
    const isManuallyUnread = wasManuallyMarkedUnread(notification.id)

    const handleClick = () => {
        // Auto-mark-as-read only applies the first time a notification is opened,
        // and never if the user has explicitly marked it "da leggere" — even if
        // new activity later re-surfaced the dot, opening it again must not
        // silently override that manual choice.
        if (!notification.readAt && !hasBeenSeen(notification.id) && !wasManuallyMarkedUnread(notification.id)) {
            markAsRead(notification.id)
        }
        markAsSeen(notification.id)

        if (onClick) {
            onClick(notification)
        }

        if (link) {
            const url = new URL(link)
            navigate(`${url.pathname}${url.search}${url.hash}`)
        }
    }

    return (
        <StyledNotificationItem
            $isAlert={severity === 'alert'}
            $isManuallyUnread={isManuallyUnread}
            direction="row"
            align="start"
            justify="space-between"
            fullWidth
            gap={4}
            onClick={handleClick}
        >
            <Flexbox direction="column" align="start" gap={2}>
                <Flexbox direction="row" align="start" gap={2}>
                    {!notification.readAt && !hasBeenSeen(notification.id) && <StyledNotificationUnreadDot />}
                    <Flexbox direction="column" align="start" gap={2}>
                        {triggeredByUserName && (
                            <Flexbox direction="row" align="center" gap={2}>
                                <Avatar name={triggeredByUserName} size="xs" />
                                <Text size="textXs" palette="neutral" shade="700">
                                    {triggeredByUserName}
                                </Text>
                            </Flexbox>
                        )}
                        <Flexbox direction="column" gap={1}>
                            <Flexbox align="center" gap={1}>
                                {notification.notification.type === NotificationType.Enum['Conv-001'] ||
                                notification.notification.type === NotificationType.Enum['Conv-002'] ? (
                                    <MessageChatSquareIcon size={16} />
                                ) : notification.notification.type.startsWith('Age-') ? (
                                    <CalendarIcon size={16} />
                                ) : (
                                    <Monitor01Icon size={16} />
                                )}
                                {hasConversationTitle ? (
                                    <Flexbox direction="column" align="start" gap={0}>
                                        <Text size="textXs" weight="500" palette="neutral" shade="900">
                                            {conversationTitleLabel}
                                        </Text>
                                        <Text size="textMd" weight="500" palette="neutral" shade="900">
                                            "{extraData?.title}"
                                        </Text>
                                    </Flexbox>
                                ) : (
                                    <Text size="textMd" weight="500" palette="neutral" shade="900">
                                        {title}
                                    </Text>
                                )}
                            </Flexbox>
                            {extraData && (
                                <Text size="textXs" palette="neutral" shade="700">
                                    {extraData.patientName && <span>{extraData.patientName}</span>}
                                    {extraData.projectCode && <span> • PRI n°{extraData.projectCode}</span>}
                                    {type === 'Age-001-Repeated' && <span> • {bodyTemplate}</span>}
                                </Text>
                            )}
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
            <Flexbox direction="column" align="end" gap={1}>
                <StyledMarkReadButton
                    variant={notification.readAt ? 'secondary' : 'primary'}
                    size="xs"
                    shape="rectangle"
                    style={{whiteSpace: 'nowrap'}}
                    onClick={event => {
                        event.stopPropagation()
                        const willBeRead = !notification.readAt
                        markNotification({isRead: willBeRead})
                        markAsSeen(notification.id)
                        if (willBeRead) {
                            clearManuallyUnread(notification.id)
                        } else {
                            markAsManuallyUnread(notification.id)
                        }
                    }}
                >
                    <CheckDone01Icon />
                    {notification.readAt ? t('notifications:mark_as_unread') : t('notifications:mark_as_read')}
                </StyledMarkReadButton>
                <StyledNotificationTime>{formattedTime}</StyledNotificationTime>
            </Flexbox>
        </StyledNotificationItem>
    )
}

export const NotificationItem = memo(NotificationItemComponent)
